// Bank Statement Analysis Data Transformation Utilities

// ==================== INTERFACES ====================

export interface AccountHolderDetail {
  bank_account_id: number;
  StartDate: string;
  EndDate: string;
  Period: string;
  NumberOfMonths: number;
  TotalCredits: number;
  OpeningBalance: number;
  ClosingBalance: number;
  account_name: string;
  account_number: string;
  account_type: string;
  bank_name: string;
  OD_CC_Limit: number;
  ExpensesToIncomeRatio: string;
  AverageBalanceForPeriod: string;
}

export interface BankTransaction {
  RowNumber: number;
  id: number;
  UserId: number;
  bank_account_id: number;
  L1_Labels: string; // Category level 1
  L2_Labels: string; // Category level 2
  L3_Labels: string; // Category level 3
  transaction_type: 'CREDIT' | 'DEBIT';
  description: string;
  debits: number;
  credits: number;
  current_balance: number;
  transaction_id: string;
  transaction_time: string;
  value_date: string;
  reference: string;
  created_at: string;
  updated_at: string;
  account_name: string;
  account_number: string;
  bank_name: string;
  amount?: number; // Computed field
}

export interface TopTransaction {
  description: string;
  L3_Labels: string;
  value_date: string;
  amount: number;
}

export interface FundRemittance {
  TransactionMonth: string;
  TransactionDate: string;
  Description: string;
  Amount: number;
}

export interface IncomeExpenseData {
  Month: string;
  MonthlyIncome: number;
  MonthlyExpense: number;
  CurrentBalance: number;
}

export interface DailyAverageBalance {
  Description: string;
  "Daily Average EOD Balance": number;
  "Average Monthly Business Credit": number;
}

export interface MonthlyAverageBalance {
  Description: string;
  "Average Balance": number;
}

export interface DailyEODBalance {
  Date: string;
  "EOD Balance": number;
}

export interface LoanTransactionSummary {
  TransactionDate: string;
  TotalDebits: number;
  TotalCredits: number;
}

export interface LoanTransactionDetails {
  value_date: string;
  L3_Labels: string;
  TotalDebits: number;
  TotalCredits: number;
}

export interface EMIPaymentCount {
  EMIPaymentCount: number;
  TotalEMIAmount: number;
}

export interface LoanPaymentRatio {
  account_name: string;
  bank_name: string;
  account_number: string;
  bank_account_id: number;
  TotalLoanRepayments: string;
  TotalDebits: string;
  LoanRatio: number;
}

export interface ExpenseRatio {
  account_name: string;
  bank_name: string;
  account_number: string;
  bank_account_id: number;
  TotalNonLoanExpenses: string;
  TotalDebits: string;
  ExpenseRatio: number;
}

export interface InvestmentRatio {
  account_name: string;
  bank_name: string;
  account_number: string;
  bank_account_id: number;
  TotalInvestment: string;
  TotalCredits: string;
  InvestmentRatio: number;
}

export interface InvestmentTransaction {
  value_date: string;
  L3_Labels: string;
  description: string;
  Credit: number;
  Debit: number;
}

export interface ExceptionalTransaction {
  Category: string;
  Number: number;
  TransactionType: string;
}

export interface IncomeConsistency {
  Month: string;
  MonthlyIncome: number;
  StandardDeviation: string;
  ConsistencyRating: string;
  PreviousMonthIncome: number;
  AmountDifference: number;
  IncomeTrendRating: string;
}

export interface HighValueTransaction {
  value_date: string;
  avrageincome: number;
  debits: number;
  IsHighValue: string;
}

export interface MonthlyOverdraft {
  MonthYear: string;
  account_name: string;
  account_number: string;
  bank_name: string;
  OD_Limit: number;
  OverdraftCount: number;
  FrequentOverdraftFlag: string;
}

export interface IrregularIncome {
  value_date: string;
  account_name: string;
  account_number: string;
  bank_name: string;
  description: string;
  credits: number;
  MonthYear: string;
  IrregularIncomeFlag: string;
}

export interface SuddenLargeWithdrawal {
  account_name: string;
  account_number: string;
  bank_name: string;
  description: string;
  debits: number;
  value_date: string;
  MonthYear: string;
  SuddenLargeWithdrawalFlag: string;
}

export interface CreditWorthinessSummary {
  Month: string;
  AverageMonthlyBalance: number;
  IncomeConsistencyRating: string;
  SavingsRate: string;
  DiscretionarySpendingFlag: string;
  DiscretionaryProportion: string;
  TotalScore: number;
  RiskLevel: string;
}

export interface MonthlyAnalysis {
  Month: string;
  TotalDebits: string;
  NoOfDebits: string;
  TotalCredits: string;
  NoOfCredits: string;
  MinDebit: string;
  MinCredit: string;
  MaxDebit: string;
  MaxCredit: string;
  AvgDebit: string;
  AverageCredit: string;
  Avgutilization: string;
  EOMBalance: string; // End of month balance
}

export interface BankStatementData {
  // Raw data sections
  accountHolderDetails: AccountHolderDetail[];
  bankTransactions: BankTransaction[];
  top10Credits: TopTransaction[];
  top10Debits: TopTransaction[];
  topFundRemittances: FundRemittance[];
  below5Remittance: FundRemittance[];
  topFundCredit: FundRemittance[];
  topLowestFundCredit: FundRemittance[];
  loanTransactionSummary: LoanTransactionSummary[];
  loanTransactionDetails: LoanTransactionDetails[];
  incomeExpense: IncomeExpenseData[];
  dailyAverageBalance: DailyAverageBalance[];
  monthlyAverageBalance: MonthlyAverageBalance[];
  monthlyAnalysis: MonthlyAnalysis[];
  dailyEODBalance: DailyEODBalance[];
  monthlyIncomeExpense: IncomeExpenseData[];
  emiPaymentCount: EMIPaymentCount[];
  loanPaymentRatio: LoanPaymentRatio[];
  expenseRatio: ExpenseRatio[];
  investmentRatioRepeater: InvestmentRatio[];
  investmentTransactions: InvestmentTransaction[];
  exceptionalTransactions: ExceptionalTransaction[];
  incomeConsistency: IncomeConsistency[];
  highValueTransactions: HighValueTransaction[];
  monthlyOverdraft: MonthlyOverdraft[];
  irregularIncome: IrregularIncome[];
  suddenLargeWithdrawal: SuddenLargeWithdrawal[];
  creditWorthinessSummary: CreditWorthinessSummary[];
  allKPIs: BankTransaction[];
  savingsRate: any[];
  salaryConsistency: any[];
  summaryOfLoanTransactions: LoanTransactionSummary[];
  detailsOfLoanSummary: LoanTransactionDetails[];

  // Computed fields for summary
  averageBalance: number;
  totalCredits: number;
  totalDebits: number;
  expensesToIncomeRatio: string;
  period: string;
  netCashFlow: number;
  averageMonthlyIncome: number;
  averageMonthlyExpense: number;
  transactionCount: number;
  overdraftFrequency: number;
  bounceCount: number;
  emiCount: number;
  riskScore: number;
  creditScore: number;
  savingsRatePercentage: number;
  incomeConsistencyScore: number;
  investmentRatio: number;
  loanToIncomeRatio: number;
}

// ==================== TRANSFORMATION FUNCTIONS ====================

/**
 * Transform combined BSA API responses to structured BankStatementData
 * @param analysisData - Data from /v1/finalyze/analysis endpoint
 * @param transactionsData - Data from /v1/finalyze/transactions endpoint
 */
export const transformBSAData = (analysisData: any, transactionsData?: any): BankStatementData => {
  const rawData = analysisData; // Keep original for backwards compatibility
  
  // Define which keys should come from the transactions endpoint
  const TRANSACTION_KEYS = [
    'BankTransactions',
    'Top10Credits', 
    'Top10Debits',
    'TopFundRemittances',
    'Below_5Remitnace',
    'TopFundCredit',
    'TopLoswestFundCredit',
    'LoanTransactionSummary',
    'LoanTransactionDetails',
    'EMIPaymentCount',
    'Cheque Return Transactions',
    'Investment Transactions',
    'Exceptional Transactions'
  ];
  
  // If transactionsData is provided, merge transaction-specific fields
  if (transactionsData) {
    console.log('Merging transactions data with analysis data');
    
    // Override transaction-related fields with data from transactions endpoint
    TRANSACTION_KEYS.forEach(key => {
      if (transactionsData[key] !== undefined) {
        rawData[key] = transactionsData[key];
        console.log(`Updated ${key} from transactions endpoint`);
      }
    });
    
    // Also merge any additional keys that might be in transactions response
    Object.keys(transactionsData).forEach(key => {
      if (!rawData[key] && transactionsData[key] !== null && transactionsData[key] !== undefined) {
        rawData[key] = transactionsData[key];
        console.log(`Added new key ${key} from transactions endpoint`);
      }
    });
  } else {
    console.log('No transactions data provided, using analysis data only');
  }
  
  try {
    console.log('Transforming BSA data:', rawData);

    // Extract data sections with exact key matching from API response
    const accountHolderDetails = rawData['Account holder detail'] || []; // Fixed key name
    const bankTransactions = rawData['BankTransactions'] || [];
    const top10Credits = rawData['Top10Credits'] || [];
    const top10Debits = rawData['Top10Debits'] || [];
    const topFundRemittances = rawData['TopFundRemittances'] || [];
    const below5Remittance = rawData['Below_5Remitnace'] || []; // Keep original as fallback
    const topFundCredit = rawData['TopFundCredit'] || [];
    const topLowestFundCredit = rawData['TopLoswestFundCredit'] || []; // Keep original as fallback
    const loanTransactionSummary = rawData['LoanTransactionSummary'] || [];
    const loanTransactionDetails = rawData['LoanTransactionDetails'] || [];
    const incomeExpense = rawData['IncomeExpense'] || [];
    const dailyAverageBalance = rawData['DailyAverageBalance'] || [];
    const monthlyAverageBalance = rawData['Monthly Average Balance'] || [];
    const monthlyAnalysis = rawData['Monthly Analysis'] || [];
    const dailyEODBalance = rawData['Daily EOD Balance'] || [];
    const monthlyIncomeExpense = rawData['Monthly Income And Expense'] || [];
    const emiPaymentCount = rawData['EMI Payment Count'] || [];
    const loanPaymentRatio = rawData['LoanPaymentRatio'] || [];
    const expenseRatio = rawData['ExpenseRatio'] || [];
    const investmentRatioRepeater = Array.isArray(rawData['InvestmentRatioRepeater']) 
      ? rawData['InvestmentRatioRepeater'] 
      : [];
    const investmentTransactions = Array.isArray(rawData['Investment Transactions']) 
      ? rawData['Investment Transactions'] 
      : [];
    const exceptionalTransactions = rawData['Exceptional Transactions'] || [];
    const incomeConsistency = Array.isArray(rawData['Income Consistency & Income Growth']) 
      ? rawData['Income Consistency & Income Growth'] 
      : [];
    const highValueTransactions = rawData['HighValue Transax'] || [];
    const monthlyOverdraft = rawData['Monthly Frequent Overdraft'] || [];
    const irregularIncome = rawData['Irregular Income'] || [];
    const suddenLargeWithdrawal = rawData['Sudden Large Withdrawal'] || [];
    const creditWorthinessSummary = Array.isArray(rawData['Summary Of Credit Worthiness']) 
      ? rawData['Summary Of Credit Worthiness'] 
      : [];
    
    // Debug logging
    console.log('Raw creditWorthinessSummary:', rawData['Summary Of Credit Worthiness']);
    console.log('Processed creditWorthinessSummary:', creditWorthinessSummary);
    const allKPIs = rawData['ALL KPIs'] || [];
    const savingsRate = rawData['Savings Rate'] || [];
    const salaryConsistency = rawData['Salary Consistency'] || [];
    const summaryOfLoanTransactions = rawData['Summary of Loan Transactions'] || [];
    const detailsOfLoanSummary = rawData['Details of Loan Summary'] || [];

    // Get primary account details
    const primaryAccount = accountHolderDetails[0] || {};

    // Compute key metrics
    const averageBalance = parseFloat(
      primaryAccount.AverageBalanceForPeriod?.replace(/[₹,]/g, '') || '0'
    );
    
    const totalCredits = primaryAccount.TotalCredits || 0;
    
    // Calculate total debits - use primary account data if available
    const totalDebits = bankTransactions
      .filter((t: any) => t.transaction_type === 'DEBIT')
      .reduce((sum: number, t: any) => sum + (parseFloat(t.debits) || 0), 0) || 
      parseFloat(primaryAccount.TotalDebits?.replace(/[₹,]/g, '') || '0');

    // Calculate net cash flow
    const netCashFlow = totalCredits - totalDebits;

    // Calculate average monthly income and expense from monthly income expense data
    const avgMonthlyIncome = monthlyIncomeExpense.length > 0
      ? monthlyIncomeExpense.reduce((sum: number, month: any) => 
          sum + (month.MonthlyIncome || 0), 0) / monthlyIncomeExpense.length
      : 0;

    const avgMonthlyExpense = monthlyIncomeExpense.length > 0
      ? monthlyIncomeExpense.reduce((sum: number, month: any) => 
          sum + (month.MonthlyExpense || 0), 0) / monthlyIncomeExpense.length
      : 0;

    // Top credits and debits are already in proper format from API
    const enhancedTop10Credits = top10Credits;
    const enhancedTop10Debits = top10Debits;

    // Calculate comprehensive risk indicators
    const bounceCount = exceptionalTransactions.filter((t: any) => 
      t.Category?.toLowerCase().includes('bounce') ||
      t.TransactionType?.toLowerCase().includes('bounce')
    ).length;

    const emiTotalCount = emiPaymentCount.reduce((sum: number, emi: any) => 
      sum + (emi.EMIPaymentCount || 0), 0);

    const overdraftFrequency = monthlyOverdraft.reduce((sum: number, od: any) => 
      sum + (od.OverdraftCount || 0), 0);

    // Calculate advanced metrics
    const savingsRatePercentage = creditWorthinessSummary.length > 0 
      ? parseFloat(creditWorthinessSummary[0].SavingsRate || '0')
      : 0;

    const incomeConsistencyScore = incomeConsistency.filter((ic: any) => 
      ic.ConsistencyRating === 'Stable'
    ).length / Math.max(incomeConsistency.length, 1) * 100;

    const investmentRatio = investmentRatioRepeater.length > 0 
      ? investmentRatioRepeater[0].InvestmentRatio || 0
      : 0;

    const loanToIncomeRatio = loanPaymentRatio.length > 0 
      ? loanPaymentRatio[0].LoanRatio || 0
      : 0;

    // Enhanced risk score calculation (0-100, lower is better)
    let riskScore = 30; // Start with low risk
    
    // Income consistency factors
    if (incomeConsistencyScore < 50) riskScore += 20;
    else if (incomeConsistencyScore < 75) riskScore += 10;
    
    // Financial behavior factors
    if (bounceCount > 0) riskScore += Math.min(bounceCount * 15, 30);
    if (overdraftFrequency > 2) riskScore += Math.min((overdraftFrequency - 2) * 10, 25);
    if (savingsRatePercentage < 10) riskScore += 15;
    else if (savingsRatePercentage < 20) riskScore += 8;
    
    // High-risk indicators
    if (irregularIncome.filter((ii: any) => ii.IrregularIncomeFlag === 'Yes').length > 0) riskScore += 12;
    if (suddenLargeWithdrawal.filter((slw: any) => slw.SuddenLargeWithdrawalFlag === 'Yes').length > 0) riskScore += 10;
    if (highValueTransactions.filter((hvt: any) => hvt.IsHighValue === 'Yes').length > 3) riskScore += 8;
    
    riskScore = Math.min(riskScore, 100); // Cap at 100

    // Enhanced credit score calculation (300-850, higher is better)
    let creditScore = creditWorthinessSummary.length > 0 && creditWorthinessSummary[0].TotalScore
      ? Math.round(300 + (creditWorthinessSummary[0].TotalScore / 10) * 550) // Scale 0-10 to 300-850
      : 650; // Default score
    
    // Additional credit score adjustments
    if (bounceCount === 0) creditScore += 30;
    if (overdraftFrequency === 0) creditScore += 25;
    if (savingsRatePercentage > 20) creditScore += 40;
    else if (savingsRatePercentage > 10) creditScore += 20;
    if (incomeConsistencyScore > 80) creditScore += 35;
    else if (incomeConsistencyScore > 60) creditScore += 20;
    if (emiTotalCount > 0 && bounceCount === 0) creditScore += 25; // Good EMI payment history
    if (averageBalance > 50000) creditScore += 25;
    else if (averageBalance > 10000) creditScore += 15;
    
    creditScore = Math.max(300, Math.min(creditScore, 850)); // Clamp between 300-850

    return {
      // Raw data sections
      accountHolderDetails,
      bankTransactions,
      top10Credits: enhancedTop10Credits,
      top10Debits: enhancedTop10Debits,
      topFundRemittances,
      below5Remittance,
      topFundCredit,
      topLowestFundCredit,
      loanTransactionSummary,
      loanTransactionDetails,
      incomeExpense,
      dailyAverageBalance,
      monthlyAverageBalance,
      monthlyAnalysis,
      dailyEODBalance,
      monthlyIncomeExpense,
      emiPaymentCount,
      loanPaymentRatio,
      expenseRatio,
      investmentRatioRepeater,
      investmentTransactions,
      exceptionalTransactions,
      incomeConsistency,
      highValueTransactions,
      monthlyOverdraft,
      irregularIncome,
      suddenLargeWithdrawal,
      creditWorthinessSummary,
      allKPIs,
      savingsRate,
      salaryConsistency,
      summaryOfLoanTransactions,
      detailsOfLoanSummary,

      // Computed fields
      averageBalance,
      totalCredits,
      totalDebits,
      expensesToIncomeRatio: primaryAccount.ExpensesToIncomeRatio || '0%',
      period: primaryAccount.Period || 'Unknown',
      netCashFlow,
      averageMonthlyIncome: avgMonthlyIncome,
      averageMonthlyExpense: avgMonthlyExpense,
      transactionCount: bankTransactions.length,
      overdraftFrequency,
      bounceCount,
      emiCount: emiTotalCount,
      riskScore,
      creditScore,
      savingsRatePercentage,
      incomeConsistencyScore,
      investmentRatio,
      loanToIncomeRatio
    };

  } catch (error) {
    console.error('Error transforming BSA data:', error);
    
    // Return default structure on error
    return {
      accountHolderDetails: [],
      bankTransactions: [],
      top10Credits: [],
      top10Debits: [],
      topFundRemittances: [],
      below5Remittance: [],
      topFundCredit: [],
      topLowestFundCredit: [],
      loanTransactionSummary: [],
      loanTransactionDetails: [],
      incomeExpense: [],
      dailyAverageBalance: [],
      monthlyAverageBalance: [],
      monthlyAnalysis: [],
      dailyEODBalance: [],
      monthlyIncomeExpense: [],
      emiPaymentCount: [],
      loanPaymentRatio: [],
      expenseRatio: [],
      investmentRatioRepeater: [],
      investmentTransactions: [],
      exceptionalTransactions: [],
      incomeConsistency: [],
      highValueTransactions: [],
      monthlyOverdraft: [],
      irregularIncome: [],
      suddenLargeWithdrawal: [],
      creditWorthinessSummary: [],
      allKPIs: [],
      savingsRate: [],
      salaryConsistency: [],
      summaryOfLoanTransactions: [],
      detailsOfLoanSummary: [],
      averageBalance: 0,
      totalCredits: 0,
      totalDebits: 0,
      expensesToIncomeRatio: '0%',
      period: 'Unknown',
      netCashFlow: 0,
      averageMonthlyIncome: 0,
      averageMonthlyExpense: 0,
      transactionCount: 0,
      overdraftFrequency: 0,
      bounceCount: 0,
      emiCount: 0,
      riskScore: 100,
      creditScore: 300,
      savingsRatePercentage: 0,
      incomeConsistencyScore: 0,
      investmentRatio: 0,
      loanToIncomeRatio: 0
    };
  }
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Format currency in Indian Rupees
 */
export const formatCurrency = (amount: number | string): string => {
  const num = typeof amount === 'string' ? parseFloat(amount.replace(/[₹,]/g, '')) : amount;
  if (isNaN(num)) return '₹0';
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

/**
 * Format number with Indian number system
 */
export const formatNumber = (num: number | string): string => {
  const number = typeof num === 'string' ? parseFloat(num.replace(/[₹,]/g, '')) : num;
  if (isNaN(number)) return '0';
  
  return new Intl.NumberFormat('en-IN').format(number);
};

/**
 * Get risk level based on risk score
 */
export const getRiskLevel = (riskScore: number): { level: string; color: string; description: string } => {
  if (riskScore <= 30) {
    return {
      level: 'Low Risk',
      color: 'text-green-600 bg-green-50 border-green-200',
      description: 'Excellent financial behavior with minimal risk indicators'
    };
  } else if (riskScore <= 50) {
    return {
      level: 'Medium Risk',
      color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      description: 'Moderate risk with some areas for improvement'
    };
  } else if (riskScore <= 70) {
    return {
      level: 'High Risk',
      color: 'text-orange-600 bg-orange-50 border-orange-200',
      description: 'Elevated risk requiring attention to financial habits'
    };
  } else {
    return {
      level: 'Very High Risk',
      color: 'text-red-600 bg-red-50 border-red-200',
      description: 'Significant risk factors present, immediate attention needed'
    };
  }
};

/**
 * Get credit score grade
 */
export const getCreditScoreGrade = (creditScore: number): { grade: string; color: string; description: string } => {
  if (creditScore >= 750) {
    return {
      grade: 'Excellent',
      color: 'text-green-600',
      description: 'Outstanding credit profile'
    };
  } else if (creditScore >= 700) {
    return {
      grade: 'Good',
      color: 'text-blue-600',
      description: 'Good credit standing'
    };
  } else if (creditScore >= 650) {
    return {
      grade: 'Fair',
      color: 'text-yellow-600',
      description: 'Average credit profile'
    };
  } else if (creditScore >= 600) {
    return {
      grade: 'Poor',
      color: 'text-orange-600',
      description: 'Below average credit'
    };
  } else {
    return {
      grade: 'Very Poor',
      color: 'text-red-600',
      description: 'Poor credit standing'
    };
  }
};

/**
 * Calculate date range for analysis (oldest timestamp to next year)
 */
export const getAnalysisDateRange = (): { startDate: string; endDate: string } => {
  // Use oldest possible timestamp (1900)
  const startDate = new Date('1900-06-05T09:59:04.357Z');
  
  // Use next year timestamp
  const endDate = new Date();
  endDate.setFullYear(endDate.getFullYear() + 1);
  
  return {
    startDate: startDate.toISOString(), // Full ISO format: 2020-06-05T09:59:04.357Z
    endDate: endDate.toISOString() // Full ISO format for next year
  };
};

/**
 * Parse API error for bank statement operations
 */
export const parseBSAError = (error: any): string => {
  if (error.response) {
    const status = error.response.status;
    const responseData = error.response.data;
    
    switch (status) {
      case 400:
        return 'Invalid request data. Please check your input and try again.';
      case 422:
        return 'Invalid file format or corrupted PDF. Please ensure:\n• Files are valid PDF documents\n• PDF password is correct (if required)\n• Bank name matches the statement\n• Account type is correct';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error occurred. Please try again later.';
      default:
        return responseData.message || responseData.error || `API Error (${status})`;
    }
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};
